import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useARIA } from '../contexts/ARIAContext';

const ARIAFloatingButton: React.FC = () => {
  const { isOpen, setIsOpen, isThinking, isListening, isVoiceActive, setIsVoiceActive, startListening, stopListening } = useARIA();

  const handleToggle = () => {
    if (!isOpen) {
      setIsOpen(true);
      if (isVoiceActive) startListening();
    } else {
      setIsOpen(false);
      stopListening();
    }
  };

  return (
    <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end gap-3">
      {/* Voice Status indicator when closed but active */}
      {!isOpen && isVoiceActive && (
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-3 h-3 rounded-full bg-brand-teal shadow-[0_0_10px_rgba(45,212,191,0.5)]"
        />
      )}

      {/* Label/Hint */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="px-3 py-1 bg-black/40 backdrop-blur-md rounded-lg border border-white/10 text-xs text-white/50 pointer-events-none"
          >
            ARIA Command [Cmd+K]
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative">
        {/* Voice Toggle Mini Button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          onClick={() => setIsVoiceActive(!isVoiceActive)}
          className={`absolute -left-12 bottom-2 w-10 h-10 rounded-full border border-white/10 backdrop-blur-md flex items-center justify-center transition-colors ${
            isVoiceActive ? 'bg-brand-teal/20 text-brand-teal' : 'bg-black/40 text-white/40'
          }`}
        >
          {isVoiceActive ? '🎤' : '🔇'}
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleToggle}
          className={`relative w-14 h-14 rounded-full flex items-center justify-center transition-shadow duration-500 ${
            isOpen 
              ? 'bg-brand-plum shadow-[0_0_20px_rgba(110,84,244,0.4)]' 
              : 'bg-brand-blue shadow-[0_0_20px_rgba(30,64,175,0.4)]'
          }`}
        >
          {/* Glow Ring for Thinking/Listening */}
          {(isThinking || isListening) && (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
              className={`absolute inset-0 rounded-full border-2 border-r-transparent border-b-transparent border-l-transparent ${
                isListening ? 'border-t-brand-teal' : 'border-t-white/40'
              }`}
            />
          )}
          
          {/* Central "A" Logo */}
          <span className="text-2xl font-bold text-white select-none">
            {isOpen ? '✕' : 'A'}
          </span>
        </motion.button>
      </div>
    </div>
  );
};

export default ARIAFloatingButton;
